// Portraits in a look (character look, 2026-10-29): the friends list, the login circles and the licence photo are
// canvas pictures (friendPortrait.js), so each part is recoloured on the CPU from its packed mask
// (/assets/anim/portrait/<key>_mask.webp) with the shader's maths (lookPalette.mjs lookPixel).
import { lookPixel, maskCodeAt, maskWeights } from './lookPalette.mjs';
import { resolveLook, isDefaultLook } from './lookCatalog.mjs';
import { cutSheet, hairFallback, hairKeys, itemLift, ITEM_FIT } from './hairArt.mjs';

// bottom → top, as NatalkaSprite stacks them (zOrderFor): both wear their trousers over their shoes; the hat over
// the front hair
const LAYERS = { ken: ['buty', 'spodniczka', 'bluzka', 'kurtka'], natalka: ['buty', 'spodniczka', 'bluzka', 'kurtka'] };

/** A portrait's parts, bottom to top: back hair, the body, the default's neck, the garments, front hair, the hat —
 *  from the copy the walk draws it from on this style (hairArt cutSheet: a hat's bare copy, one cut for a tail). */
export function portraitKeys(character, equipped, look, hair = hairKeys('portrait', character, look, equipped)) {
  const worn = (LAYERS[character] ?? LAYERS.natalka).map((c) => equipped?.[c]).filter(Boolean);
  const walk = (id) => `${character}_walk_${id}`;       // walkAnim.js WALK.garment (which needs Phaser)
  const item = equipped?.akcesorium;
  return [hair.back, walk('body'), ...(hair.neck ? [hair.neck] : []), ...worn.map(walk), hair.front,
    ...(item ? [cutSheet(walk(item), character, look, item)] : [])];
}

/** How many px higher each of `keys` (portraitKeys) draws: the hat as the walk lifts it standing towards the camera
 *  (hairArt itemLift, the 'front' view the portrait is cut from), only over the style's own hair. */
export function portraitLifts(keys, character, equipped, look, hair = hairKeys('portrait', character, look, equipped)) {
  const item = equipped?.akcesorium;
  const up = item && hair.front !== hairFallback(character).front ? itemLift('walk', character, look, item, ITEM_FIT, 'front') : 0;
  return keys.map((_, i) => (item && i === keys.length - 1 ? up : 0));
}

/** One cache entry per picture: the parts, the crop and — only when it changes anything — the look. */
export function portraitCacheKey(keys, crop, character, look) {
  const base = keys.join('|') + `@${crop.w}x${crop.h}`;
  return isDefaultLook(character, look) ? base : `${base}#${JSON.stringify(resolveLook(character, look))}`;
}

/** Recolour a part's RGBA pixels (getImageData, straight alpha) in place, `w` x `h`, from its mask's RGBA bytes
 *  (`maskW` texels wide, six pixels per texel) for the look's uniforms (lookUniforms, non-null). */
export function recolourImageData(data, w, h, mask, maskW, uniforms) {
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const code = maskCodeAt(mask, maskW, x, y);
      const i = (y * w + x) * 4;
      if (!code || !data[i + 3]) continue;
      const c = lookPixel([data[i] / 255, data[i + 1] / 255, data[i + 2] / 255], maskWeights(code), uniforms);
      data[i] = Math.round(c[0] * 255); data[i + 1] = Math.round(c[1] * 255); data[i + 2] = Math.round(c[2] * 255);
    }
  }
  return data;
}
